import type { CreateEventFormData } from "../_types/wizard-types";

/**
 * `sessionStorage` key under which the wizard persists in-progress form data
 * across the guest → auth → resume roundtrip. Versioned so a future wizard
 * schema change can bump it and naturally invalidate stale blobs.
 */
export const WIZARD_STORAGE_KEY = "create_event_wizard_v4";

/**
 * `sessionStorage` key for the opaque Northstar request id. Persisted separately from the form blob
 * so the welcome popup can re-show on a plain refresh while the id stays OUT of the URL (it's stripped
 * after arrival so it can't leak via `Referer` or a shared link).
 */
export const NORTHSTAR_ID_STORAGE_KEY = "create_event_northstar_ns_id";

// -- SSR-safe sessionStorage primitives (no-ops on the server; never throw) --

/** Writes a value to sessionStorage. SSR-safe; swallows quota/security errors. */
function setSessionItem(key: string, value: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch (err) {
    console.warn(`wizard-storage: failed to write ${key}`, err);
  }
}

/** Reads a value from sessionStorage, or `null` if absent/unavailable. SSR-safe. */
function getSessionItem(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch (err) {
    console.warn(`wizard-storage: failed to read ${key}`, err);
    return null;
  }
}

/** Removes a key from sessionStorage. SSR-safe. */
function removeSessionItem(key: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(key);
  } catch (err) {
    console.warn(`wizard-storage: failed to remove ${key}`, err);
  }
}

/**
 * Persists wizard form data to sessionStorage so the user can resume after
 * being redirected through the auth flow. SSR-safe (no-op on the server).
 *
 * @param data - The full wizard form state.
 */
export function saveWizardState(data: CreateEventFormData): void {
  setSessionItem(WIZARD_STORAGE_KEY, JSON.stringify(data));
}

/**
 * Reads previously saved wizard form data. Returns `null` if nothing is saved
 * or if the saved blob is corrupt (e.g., from a prior wizard schema). SSR-safe.
 *
 * @returns The saved form data or `null`.
 */
export function loadWizardState(): CreateEventFormData | null {
  const raw = getSessionItem(WIZARD_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CreateEventFormData;
    // Shape check — bail if the structure doesn't match the current wizard schema.
    // Covers the fields added in v3 (numPeople, flexibility, meetingRooms.numRooms)
    // so a stale blob from an older shape can't hydrate a broken resume even if the
    // versioned key somehow survives.
    if (
      typeof parsed?.name !== "string" ||
      !Array.isArray(parsed.formattedLocations) ||
      typeof parsed.numPeople !== "number" ||
      !("flexibility" in parsed) ||
      typeof parsed.meetingRooms?.numRooms !== "number"
    ) {
      return null;
    }
    return parsed;
  } catch (err) {
    console.warn("wizard-storage: failed to parse saved state", err);
    return null;
  }
}

/**
 * Clears any saved wizard state. Call this after a successful submission so
 * the next /create visit starts fresh. SSR-safe.
 */
export function clearWizardState(): void {
  removeSessionItem(WIZARD_STORAGE_KEY);
}

/**
 * Persists the Northstar request id for the current session. SSR-safe (no-op on the server).
 *
 * @param nsId - The opaque `draft_events.uuid` carried from the Northstar redirect.
 */
export function saveNorthstarId(nsId: string): void {
  setSessionItem(NORTHSTAR_ID_STORAGE_KEY, nsId);
}

/**
 * Reads the persisted Northstar request id for this session, or `null` if none. SSR-safe.
 *
 * @returns The stored opaque id or `null`.
 */
export function loadNorthstarId(): string | null {
  return getSessionItem(NORTHSTAR_ID_STORAGE_KEY);
}

/**
 * Clears the persisted Northstar id. Call after event creation so a subsequent same-session visit to
 * /create doesn't resurface the Northstar welcome. SSR-safe.
 */
export function clearNorthstarId(): void {
  removeSessionItem(NORTHSTAR_ID_STORAGE_KEY);
}
